import { Button, Card, Col, Divider, message, Row, Select, Spin } from 'antd';
import { Form } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';

import useClients from '../../../hooks/useClients';
import api from '../../../services/api';
import { useProposalStore } from '../../../store/proposalStore';
import ClientModal from './components/proposal/ClientModal';
import FeeModal from './components/proposal/FeeModal';
import FeeTable from './components/proposal/FeeTable';
import LogoUploader from './components/proposal/LogoUploader';
import ProposalForm from './components/proposal/ProposalForm';
// COMPONENTES MODULARES
import ProposalPreview from './components/proposal/ProposalPreview';

const ProposalGenerator: React.FC = () => {
  // Formulários
  const [form] = Form.useForm();
  const [feeForm] = Form.useForm();
  const [clientForm] = Form.useForm();

  // Zustand store
  const {
    fees,
    clients,
    selectedClient,
    logo,
    proposalData,
    setLogo,
    setSelectedClient,
    updateProposalField,
    fetchExchangeRates,
    addFee,
    updateFeeValue,
    deleteFee,
    setFees,
  } = useProposalStore();

  // Idioma
  const [language, setLanguage] = useState<'pt' | 'en'>('en');

  // Estados locais
  const [isFeeModalVisible, setIsFeeModalVisible] = useState(false);
  const [editingFee, setEditingFee] = useState<{ group: string, index: number, fee: any } | null>(null);
  const [isClientModalVisible, setIsClientModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [categoryGroup, setCategoryGroup] = useState<string | any>(null);
  const pdfRef = useRef<HTMLDivElement>(null);
  const isUpdatingForm = useRef(false);

  // Importa o hook customizado de clientes
  const { fetchClients, addNewClient } = useClients();

  // Load dados do usuário (empresa/logado)
  useEffect(() => {
    const fetchUserData = async () => {
      const email = localStorage.getItem('user_email');
      if (!email) return;
      setLoading(true);
      try {
        const response = await api.post('/user/find_user', { email });
        if (response.data.user) setUserData(response.data.user);
      } catch {
        message.error('Erro ao buscar dados do usuário');
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    fetchClients(); // Só aqui! NÃO dentro do hook!
  }, []);

  // Carregar logo da empresa (do localStorage)
  useEffect(() => {
    const savedLogo = localStorage.getItem('companyLogo');
    if (savedLogo) setLogo(savedLogo);
  }, [setLogo]);

  // Buscar taxas iniciais (mock/exemplo)
  useEffect(() => {
    fetchExchangeRates();
  }, [fetchExchangeRates]);


  // Sincronizar formulário com store
  useEffect(() => {
    if (isUpdatingForm.current) return;
    isUpdatingForm.current = true;
    try {
      form.setFieldsValue({
        ...proposalData,
        date: proposalData.date ? dayjs(proposalData.date) : undefined,
        expiryDate: proposalData.expiryDate ? dayjs(proposalData.expiryDate) : undefined,
        sailingDate: proposalData.sailingDate ? dayjs(proposalData.sailingDate) : undefined,
      });
    } finally {
      isUpdatingForm.current = false;
    }
  }, [proposalData, form]);

  // Handlers para taxas
  const handleEditFee = (group: string, index: number, fee: any) => {
    setEditingFee({ group, index, fee });
    setCategoryGroup(group);
    feeForm.setFieldsValue({
      name: fee.name ?? fee.displayName,
      group: fee.category,
      currency: fee.currency,
      value: fee.value,
    });

    setIsFeeModalVisible(true);
  };


  const handleDeleteFee = (group: string, index: number) => {
    deleteFee(group, index);
    message.success('Taxa removida com sucesso!');
  };

  const handleAddFee = (values: any) => {
    if (!editingFee) {
      const newFee = {
        name: values.name,
        category: values.group,
        category_name: values.group,
        currency: values.currency,
        value: values.value,
      };

      addFee(values.group, newFee);
    } else {
      handleUpdateFee(values);
      return;
    }

    setIsFeeModalVisible(false);
    setEditingFee(null);
    feeForm.resetFields();
    message.success('Taxa adicionada com sucesso!');
  };

  const handleUpdateFee = (values: any) => {
    if (!editingFee) return;

    const { group: oldGroup, index, fee: originalFee } = editingFee;

    const newGroup = values.group;

    const updatedFee = {
      ...originalFee,
      name: values.name,
      category: newGroup,
      category_name: newGroup,
      currency: values.currency,
      value: values.value,
    };

    const newFees: any = { ...fees };
    newFees[oldGroup] = [...newFees[oldGroup]];
    newFees[oldGroup].splice(index, 1);

    if (!newFees[newGroup]) newFees[newGroup] = [];
    newFees[newGroup] = [...newFees[newGroup], updatedFee];

    setFees(newFees);

    setIsFeeModalVisible(false);
    setEditingFee(null);
    feeForm.resetFields();
    message.success("Taxa atualizada com sucesso!");
  };

  // Salvar novo cliente
  const handleAddClient = async (values: any) => {
    setLoadingClient(true);
    try {
      await addNewClient(values);
      setIsClientModalVisible(false);
      clientForm.resetFields();
    } finally {
      setLoadingClient(false);
    }
  };

  // Calcular total das taxas (em USD)
  const calculateTotal = () => {
    let total = 0;
    Object.values(fees).forEach(feeGroup => {
      feeGroup.forEach(fee => {
        const usdValue = fee.currency === 'USD' ? fee.value :
          fee.currency === 'EUR' ? fee.value * 1.1 :
            fee.currency === 'BRL' ? fee.value * 0.18 : fee.value;
        total += usdValue;
      });
    });
    return total;
  };

  // Gerar PDF do preview
  const generatePDF = async () => {
    if (!selectedClient) {
      message.error('Selecione um cliente antes de gerar o PDF');
      return;
    }
    if (!pdfRef.current) {
      message.error('Erro ao gerar PDF');
      return;
    }
    try {
      setLoading(true);
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      const canvas = await html2canvas(pdfRef.current, { scale: 3, useCORS: true, allowTaint: true });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      let position = 0;
      let heightLeft = imgHeight;
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`proposta-${proposalData.quotationNo || 'draft'}.pdf`);
      message.success('PDF gerado com sucesso!');
    } catch (error) {
      message.error('Erro ao gerar PDF');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Renderização principal
  return (
    <Spin spinning={loading}>
      <div style={{ padding: '24px' }}>
        {/* Barra de ações/topo */}
        <Card>
          <Row gutter={[16, 16]} align="middle">
            <Col span={16}>
              <h2>Gerador de Proposta de Frete</h2>
            </Col>
            <Col span={8} style={{ textAlign: 'right' }}>
              <div className='ml-2 flex justify-end'>
                <Select
                  value={language}
                  style={{ width: 120, marginRight: 12 }}
                  onChange={setLanguage}
                  options={[
                    { label: <span>🇧🇷 Português</span>, value: 'pt' },
                    { label: <span>🇺🇸 English</span>, value: 'en' }
                  ]}
                />
                <Button
                  type="primary"
                  style={{ marginRight: 12 }}
                  onClick={() => setIsClientModalVisible(true)}
                >
                  Novo Cliente
                </Button>
                <Button
                  type="primary"
                  onClick={generatePDF}
                  disabled={!selectedClient}
                  loading={loading}
                >
                  Gerar PDF
                </Button>
              </div>
            </Col>
          </Row>
        </Card>

        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          {/* Coluna esquerda: configs, cliente, formulário */}
          <Col span={8}>
            <Card title="Configurações" size="small">
              <LogoUploader logo={logo} setLogo={setLogo} />
              {/* Seletor de cliente */}
              <div style={{ marginBottom: 16 }}>
                <b>Cliente:</b>
                <Select
                  placeholder="Selecione um cliente"
                  style={{ width: '100%', marginTop: 8 }}
                  value={selectedClient?._id}
                  onChange={(value) => {
                    const client = clients.find(c => c._id === value);
                    setSelectedClient(client || null);
                  }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {clients.map(client => (
                    <Select.Option key={client._id} value={client._id}>
                      {client.name}
                    </Select.Option>
                  ))}
                </Select>
                {selectedClient && (
                  <div style={{
                    backgroundColor: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    marginTop: 8
                  }}>
                    <div><strong>Name:</strong> {selectedClient.name}</div>
                    <div><strong>Email:</strong> {selectedClient.email}</div>
                    <div><strong>Phone:</strong> {selectedClient.phone}</div>
                    <div><strong>Document:</strong> {selectedClient.document}</div>
                  </div>
                )}
              </div>
            </Card>
            <Card title="Dados da Proposta" size="small" style={{ marginTop: '16px' }}>
              <ProposalForm
                form={form}
                proposalData={proposalData}
                updateProposalField={updateProposalField}
                isUpdatingForm={isUpdatingForm}
              />
              <Divider />
              <div style={{
                padding: '8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                <strong>Status:</strong> {proposalData.quotationNo ? 'Dados Carregados' : 'Novo Formulário'}
                <span style={{ float: 'right' }}>
                  <strong>Última Atualização:</strong> {dayjs().format('DD/MM/YYYY HH:mm')}
                </span>
              </div>
            </Card>
          </Col>
          {/* Coluna direita: taxas */}
          <Col span={16}>
            <Card
              title="Taxas e Valores"
              size="small"
              extra={(
                <Button type="primary" onClick={() => setIsFeeModalVisible(true)}>
                  + Adicionar Taxa
                </Button>
              )}
            >
              {Object.keys(fees).length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#999'
                }}>
                  Nenhuma taxa carregada.
                </div>
              ) : (
                Object.keys(fees)
                  .filter(groupName => fees[groupName] && fees[groupName].length > 0)
                  .map((groupName, groupIndex) => {
                    const feeList = fees[groupName];
                    return (
                      <div key={`group-${groupIndex}`} style={{ marginBottom: '24px' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px'
                        }}>
                        </div>
                        <FeeTable
                          groupName={groupName}
                          feeList={feeList}
                          onEdit={handleEditFee}
                          onDelete={handleDeleteFee}
                        />
                      </div>
                    )
                  })
              )}
              {/* {Object.keys(fees).length > 0 && (
                <>
                  <Divider />
                  <Row justify="space-between" align="middle">
                    <Col>
                      <span style={{ color: '#888', fontSize: 12 }}>
                        * Conversão aproximada para USD (EUR: 1.1, BRL: 0.18)
                      </span>
                    </Col>
                    <Col>
                      <span style={{ fontWeight: 600, fontSize: 16, color: '#1890ff' }}>
                        Total: USD {calculateTotal().toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </span>
                    </Col>
                  </Row>
                </>
              )} */}
            </Card>
          </Col>
        </Row>
 
        {/* Preview do PDF */}
        {selectedClient && Object.keys(fees).length > 0 && (
          <Card title="Preview da Proposta" style={{ marginTop: '24px' }}>
            <div ref={pdfRef} style={{ background: 'white', padding: '40px' }}>
              <ProposalPreview
                logo={logo}
                client={selectedClient}
                proposalData={proposalData}
                userData={userData}
                fees={fees}
                total={calculateTotal()}
                language={language}
              />
            </div>
          </Card>
        )}
        {/* Modal de taxa */}
        <FeeModal
          visible={isFeeModalVisible}
          onCancel={() => {
            setIsFeeModalVisible(false);
            setEditingFee(null);
            feeForm.resetFields();
          }}
          onOk={editingFee ? handleUpdateFee : handleAddFee}
          initialValues={editingFee}
          categoryGroup={categoryGroup}
          feeForm={feeForm}
        />

        {/* Modal de cliente */}
        <ClientModal
          visible={isClientModalVisible}
          onCancel={() => setIsClientModalVisible(false)}
          onSave={handleAddClient}
          form={clientForm}
          loading={loadingClient}
        />
      </div>
    </Spin>
  );
};

export default ProposalGenerator;
